import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Turnstile } from "@marsidev/react-turnstile";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import HeroVisual from "@/components/HeroVisual";
import { SERVICES } from "@/data/site";
import { ArrowRight, Sparkles, Quote, Send, CheckCircle2, AlertCircle, MessageSquareQuote, Star, ThumbsUp, Building } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERVICE_OPTIONS = SERVICES.map(s => s.title);

export default function References() {
  const [refs, setRefs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [siteKey, setSiteKey] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/references`).then(r => setRefs(r.data.items || [])).catch(() => {});
    axios.get(`${API}/public/testimonials`).then(r => setTestimonials(r.data.items || [])).catch(() => {});
    axios.get(`${API}/public/site-config`).then(r => setSiteKey(r.data.turnstile_site_key || "")).catch(() => {});
  }, []);

  const logoSrc = (u) => u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;

  return (
    <>
      <SEO title="Referanslar — Pratik Teknoloji" description="Pratik Teknoloji'nin birlikte çalıştığı kurum ve işletmeler ile müşteri yorumları." />

      <PageHero
        badge="Referanslar"
        title={<>Birlikte yol aldığımız</>}
        titleAlt="işletmeler."
        subtitle="Çanakkale ve çevresindeki pek çok işletmenin bilişim altyapılarını kuruyor ve yönetiyoruz. Aşağıda birlikte çalıştığımız firmalar ve müşterilerimizden gelen yorumlar yer alıyor."
        rightSlot={
          <HeroVisual
            accentColor="#34C759"
            center={{ icon: <Star size={36} strokeWidth={1.6} />, topLabel: "Müşteri", bottomLabel: "Güveni" }}
            cards={[
              { position: "tr", rotate: 4, delay: 0.4, color: "#FF3B30", icon: <Building size={16} />, label: "Firma", value: `${Math.max(refs.length, 0)}+`, sub: "aktif kurumsal referans" },
              { position: "ml", rotate: -5, delay: 0.55, color: "#34C759", icon: <ThumbsUp size={16} />, label: "Memnuniyet", value: "%98", sub: "memnuniyet oranı" },
              { position: "br", rotate: 3, delay: 0.7, color: "#007AFF", icon: <MessageSquareQuote size={16} />, label: "Yorumlar", value: `${Math.max(testimonials.length, 0)}+`, sub: "onaylı müşteri yorumu" },
            ]}
          />
        }
      />

      {/* Logo grid */}
      <section className="py-16 md:py-24 bg-[#0A0A0A] border-t border-white/10" data-testid="references-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {refs.length === 0 ? (
            <div className="relative overflow-hidden rounded-[2.5rem] border border-white/10 bg-gradient-to-br from-[#1a1a1a] to-[#0A0A0A] p-14 md:p-20 text-center">
              <div className="absolute -top-24 -right-24 w-72 h-72 bg-[#FF3B30]/25 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-[#007AFF]/20 rounded-full blur-3xl"></div>
              <div className="relative inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/30 mb-6">
                <Sparkles size={28} strokeWidth={1.8} />
              </div>
              <h2 className="relative font-display text-3xl md:text-5xl font-bold tracking-tight text-white">Yakında burada.</h2>
              <p className="relative mt-5 text-white/60 max-w-xl mx-auto text-lg leading-relaxed">
                Referanslarımızın sergilendiği bu alan kısa süre içinde güncelleniyor.
              </p>
            </div>
          ) : (
            <>
              <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#FF3B30] mb-3">Birlikte Çalıştığımız İşletmeler</div>
              <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-white mb-10">
                Toplam <span className="text-[#FF3B30]">{refs.length}</span> kurumsal referans.
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-testid="references-grid">
                {refs.map((r) => (
                  <div key={r.id} className="group relative rounded-2xl bg-white/[0.04] border border-white/10 hover:border-[#FF3B30]/40 p-6 flex flex-col items-center justify-center min-h-[140px] transition">
                    {r.logo_url ? (
                      <img src={logoSrc(r.logo_url)} alt={r.name} className="max-h-16 max-w-full object-contain opacity-80 group-hover:opacity-100 transition" />
                    ) : (
                      <div className="font-display text-lg font-bold text-white/80 group-hover:text-white text-center">{r.name}</div>
                    )}
                    {r.logo_url && <div className="mt-3 text-[11px] text-white/40 group-hover:text-white/70 text-center">{r.name}</div>}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Testimonials list */}
      {testimonials.length > 0 && (
        <section className="py-16 md:py-24 bg-[#0A0A0A] border-t border-white/10" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#FF3B30] mb-3">Müşteri Yorumları</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-white mb-10">Onların sözleriyle.</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="testimonials-grid">
              {testimonials.map((t) => (
                <article key={t.id} className="rounded-2xl bg-white/[0.04] border border-white/10 p-6 md:p-7 hover:border-[#FF3B30]/30 transition">
                  <Quote className="text-[#FF3B30]" size={20} />
                  <p className="mt-4 text-white/80 leading-relaxed text-[15px]">"{t.comment}"</p>
                  <div className="mt-6 pt-5 border-t border-white/10">
                    <div className="font-semibold text-white">{t.name}</div>
                    <div className="text-xs text-white/50 mt-0.5">{t.company || ""}{t.company && t.service ? " • " : ""}{t.service || ""}</div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Submit testimonial */}
      <TestimonialForm siteKey={siteKey} />

      {/* CTA */}
      <section className="py-16 md:py-24 bg-[#0A0A0A] border-t border-white/10">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-[#1a1a1a] to-[#0A0A0A] p-10 md:p-14 text-center">
            <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-white">Birlikte çalışmak istediğiniz bir projeniz mi var?</h3>
            <div className="mt-7 flex flex-wrap gap-3 justify-center">
              <Link to="/iletisim" className="inline-flex items-center gap-2 rounded-full bg-[#FF3B30] text-white px-7 py-4 font-semibold hover:bg-white hover:text-[#0A0A0A] transition-colors">
                Projenizi Konuşalım <ArrowRight size={18}/>
              </Link>
              <Link to="/hizmetler" className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 text-white px-7 py-4 font-semibold hover:bg-white hover:text-[#0A0A0A] transition-colors">
                Hizmetleri İncele
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function TestimonialForm({ siteKey }) {
  const [form, setForm] = useState({ name: "", company: "", service: "", comment: "", website: "" });
  const [tsToken, setTsToken] = useState("");
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const tsRef = useRef(null);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.comment.trim().length < 10) {
      setStatus({ state: "error", msg: "İsim ve en az 10 karakterlik yorum girin." }); return;
    }
    if (siteKey && !tsToken) { setStatus({ state: "error", msg: "Güvenlik doğrulamasını tamamlayın." }); return; }
    setStatus({ state: "loading", msg: "" });
    try {
      const res = await axios.post(`${API}/public/testimonials`, { ...form, turnstile_token: tsToken });
      setStatus({ state: "success", msg: res.data.message || "Yorumunuz alındı." });
      setForm({ name: "", company: "", service: "", comment: "", website: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({ state: "error", msg: typeof detail === "string" ? detail : "Bir hata oluştu." });
    } finally {
      tsRef.current?.reset(); setTsToken("");
    }
  };

  return (
    <section className="py-16 md:py-24 bg-[#0A0A0A] border-t border-white/10" data-testid="testimonial-form-section">
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#FF3B30] mb-3 flex items-center gap-2"><MessageSquareQuote size={14} /> Yorum Bırakın</div>
        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-white mb-3">Deneyiminizi paylaşın.</h2>
        <p className="text-white/60 max-w-xl">Hizmetlerimizden memnun kaldıysanız bize iki satır yazın. Yorumunuz onay sonrası bu sayfada yayınlanır.</p>

        <form onSubmit={submit} data-testid="testimonial-form" className="mt-8 rounded-3xl bg-white/[0.03] border border-white/10 p-7 md:p-10 space-y-5" noValidate>
          {/* Honeypot */}
          <input type="text" name="website" value={form.website} onChange={update("website")} tabIndex={-1} autoComplete="off"
            style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", overflow: "hidden" }} aria-hidden="true" />

          <div className="grid md:grid-cols-2 gap-5">
            <Field label="İsim *" testId="testimonial-name">
              <input required value={form.name} onChange={update("name")} className="input-dark-pub" placeholder="Adınız" />
            </Field>
            <Field label="Firma" testId="testimonial-company">
              <input value={form.company} onChange={update("company")} className="input-dark-pub" placeholder="Firma adı (opsiyonel)" />
            </Field>
          </div>

          <Field label="Hizmet" testId="testimonial-service">
            <select value={form.service} onChange={update("service")} className="input-dark-pub">
              <option value="" style={{ background: "#0A0A0A", color: "#fff" }}>Bir hizmet seçin (opsiyonel)</option>
              {SERVICE_OPTIONS.map(s => <option key={s} value={s} style={{ background: "#0A0A0A", color: "#fff" }}>{s}</option>)}
            </select>
          </Field>

          <Field label="Yorumunuz *" testId="testimonial-comment">
            <textarea required rows={5} minLength={10} maxLength={1000} value={form.comment} onChange={update("comment")} className="input-dark-pub resize-y" placeholder="Deneyiminizi bizimle paylaşın..." />
          </Field>

          {siteKey && (
            <div className="flex justify-end" data-testid="testimonial-turnstile">
              <Turnstile ref={tsRef} siteKey={siteKey} onSuccess={(t) => setTsToken(t)} onError={() => setTsToken("")} onExpire={() => setTsToken("")} options={{ theme: "dark", language: "tr" }} />
            </div>
          )}

          {status.state === "success" && (
            <div className="flex items-start gap-2 rounded-xl bg-[#34C759]/10 border border-[#34C759]/30 p-4 text-sm text-[#4ADE80]" data-testid="testimonial-success">
              <CheckCircle2 size={16} className="flex-none mt-0.5" />{status.msg}
            </div>
          )}
          {status.state === "error" && (
            <div className="flex items-start gap-2 rounded-xl bg-[#FF3B30]/10 border border-[#FF3B30]/30 p-4 text-sm text-[#FF6B63]" data-testid="testimonial-error">
              <AlertCircle size={16} className="flex-none mt-0.5" />{status.msg}
            </div>
          )}

          <div className="flex justify-end">
            <button type="submit" disabled={status.state === "loading" || (siteKey && !tsToken)}
              data-testid="testimonial-submit"
              className="inline-flex items-center gap-2 rounded-full bg-[#FF3B30] text-white px-7 py-3.5 font-semibold hover:bg-white hover:text-[#0A0A0A] transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
              {status.state === "loading" ? "Gönderiliyor..." : <>Yorumu Gönder <Send size={16}/></>}
            </button>
          </div>
        </form>

        <style>{`.input-dark-pub{width:100%;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.12);border-radius:14px;padding:13px 16px;color:#fff;font-size:15px;font-family:inherit;outline:none;transition:.2s}.input-dark-pub:focus{border-color:#FF3B30;background:rgba(255,255,255,.06)}.input-dark-pub::placeholder{color:rgba(255,255,255,.35)}`}</style>
      </div>
    </section>
  );
}

function Field({ label, children, testId }) {
  return <label className="block" data-testid={testId}><span className="block text-xs font-bold tracking-[0.2em] uppercase text-white/50 mb-2">{label}</span>{children}</label>;
}
